import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ProviderType } from '../enums/commons.enum';
import { IForm, IModel } from '../models/abstractions.model';
import { Auditable, Identifiable } from '../models/commons.model';
import {
  GenderType,
  LegalPersonType,
  MaritalStatusType,
  PersonType,
} from '../enums/persons.enum';
import { TelephoneHelper } from '../helpers/telephones.helper';
import { Profile } from './profiles.model';

export class Person extends Auditable implements IModel {
  userId?: number;
  personType: PersonType = PersonType.User;
  legalPersonType: LegalPersonType = LegalPersonType.NaturalPerson;
  genderType?: GenderType;
  maritalStatus?: MaritalStatusType;
  fullName: string = '';
  name?: string;
  birthFoundationDate?: Date;
  isOwner?: boolean;
  isDeleted: boolean = false;
  personEmailList?: Array<PersonEmail>;
  personPhoneList?: Array<PersonPhone>;

  // TODO: Outras listas do Person

  constructor(other?: any) {
    super(other);

    if (other != undefined) {
      this.userId = other.userId;
      this.personType = other.personType;
      this.legalPersonType = other.legalPersonType;
      this.genderType = other.genderType;
      this.maritalStatus = other.maritalStatus;
      this.fullName = other.fullName;
      this.name = other.name;
      this.birthFoundationDate = other.birthFoundationDate;
      this.isOwner = other.isOwner;
      this.isDeleted = other.isDeleted;

      if (
        other.personEmailList != undefined &&
        other.personEmailList instanceof Array &&
        other.personEmailList.length > 0
      )
        this.personEmailList = other.personEmailList.map(
          (x: PersonEmail) => new PersonEmail(x)
        );

      if (
        other.personPhoneList != undefined &&
        other.personPhoneList instanceof Array &&
        other.personPhoneList.length > 0
      )
        this.personPhoneList = other.personPhoneList.map(
          (x: PersonPhone) => new PersonPhone(x)
        );
    }
  }
}

export class User extends Person implements IModel, IForm {
  private form?: UntypedFormGroup;
  id: number = 0;
  userId: number = 0;
  apiClientId: number = 0;
  apiId: number = 0;
  clientId: number = 0;
  email: string = '';
  telephone?: string;
  verified: number = 0;
  providerType?: ProviderType;
  username?: string;
  profileId?: number;
  profile?: Profile;
  profileName: string = '';
  token?: Token;
  screensAllowed?: string;
  airportsAllowed?: string;
  filterMovidaTransfer?: Boolean;
  permissions: any[] = [];

  constructor(other?: any) {
    super(other);
    if (other != undefined) {
      this.id = other.id;
      this.userId = other.user.id;
      this.apiClientId = other.apiClientId;
      this.apiId = other.apiId;
      this.clientId = other.clientId;
      this.screensAllowed = other.screensAllowed;
      this.airportsAllowed = other.airportsAllowed;
      this.filterMovidaTransfer = other.filterMovidaTransfer;
      this.email = other.user.email;
      this.telephone = other.telephone;
      this.verified = other.verified;
      this.providerType = other.providerType;
      this.permissions = other.permissions;
      try {
        this.name =
          other.user.username.split('.')[0].charAt(0).toUpperCase() +
          other.user.username.split('.')[0].slice(1).toLocaleLowerCase() +
          ' ' +
          other.user.username.split('.')[1].charAt(0).toUpperCase() +
          other.user.username.split('.')[1].slice(1).toLocaleLowerCase();
      } catch {
        this.name = other.user.username;
      }
      this.name = other.user.name;

      this.username = other.user.username;
      this.profileId = other.profileId;
      this.profile =
        other.profile != undefined ? new Profile(other.profile) : undefined;
      this.token =
        other.token != undefined ? new Token(other.token) : undefined;
    }
  }

  get asForm(): UntypedFormGroup {
    if (this.form == undefined) {
      this.form = new UntypedFormGroup({
        fullName: new UntypedFormControl(this.fullName ?? '', [
          Validators.required,
          Validators.maxLength(128),
        ]),
        name: new UntypedFormControl(this.name ?? '', [
          Validators.required,
          Validators.maxLength(128),
        ]),
        username: new UntypedFormControl(this.username, [
          Validators.required,
          Validators.maxLength(64),
        ]),
        telephone: new UntypedFormControl(
          this.telephone != undefined
            ? TelephoneHelper.format(this.telephone)
            : ''
        ),
        email: new UntypedFormControl(this.email ?? '', [
          Validators.required,
          Validators.email,
          Validators.maxLength(254),
        ]),
        profile: new UntypedFormControl(this.profile ?? null),
      });
    }
    return this.form;
  }

  get fromForm(): User {
    let value = this.form?.value;

    if (value != undefined) {
      this.fullName = value.fullName;
      this.name = value.name;
      this.username = value.username;
      this.telephone = value.telephone;
      this.email = value.email;
      this.profileId = value.profileId != '' ? parseInt(value.profileId) : 0;
    }
    return this;
  }
}

export class PersonEmail extends Auditable implements IModel {
  emailTypeId: number = 0;
  personId: number = 0;
  emailId: number = 0;
  email: string = '';

  constructor(other?: any) {
    super(other);
    if (other != undefined) {
      this.emailTypeId = other.emailTypeId;
      this.personId = other.personId;
      this.emailId = other.emailId;
      this.email = other.email;
    }
  }
}

export class PersonPhone extends Auditable implements IModel {
  phoneTypeId: number = 0;
  personId: number = 0;
  phoneId: number = 0;
  number: number = 0;

  constructor(other?: any) {
    super(other);
    if (other != undefined) {
      this.phoneTypeId = other.phoneTypeId;
      this.personId = other.personId;
      this.phoneId = other.phoneId;
      this.number = other.number;
    }
  }
}

export class Login implements IModel, IForm {
  private form?: UntypedFormGroup;

  username: string = '';
  password: string = '';
  recaptchaToken: string = '';

  constructor(username?: string, password?: string, recaptchaToken?: string) {
    this.username = username ?? '';
    this.password = password ?? '';
    this.recaptchaToken = recaptchaToken ?? '';
  }

  get asForm(): UntypedFormGroup {
    if (this.form == undefined) {
      this.form = new UntypedFormGroup({
        username: new UntypedFormControl(
          { value: this.username, disabled: false },
          [Validators.required, Validators.maxLength(64)]
        ),
        password: new UntypedFormControl(
          { value: this.password, disabled: false },
          [Validators.required, Validators.maxLength(64)]
        ),
      });
    }
    return this.form;
  }

  get fromForm(): Login {
    let value = this.form?.value;

    if (value != undefined) {
      this.username = value.username;
      this.password = value.password;
    }
    return this;
  }
}

export class MFAAutheticate implements IModel {
  token: string;
  recaptchaToken: string;

  constructor(token?: any, recaptchaToken?: any) {
    this.token = token;
    this.recaptchaToken = recaptchaToken;
  }
}

export class ForgotPassword implements IModel, IForm {
  private form?: UntypedFormGroup;

  email: string;

  constructor(email: string) {
    this.email = email;
  }

  get asForm(): UntypedFormGroup {
    if (this.form == null) {
      this.form = new UntypedFormGroup({
        email: new UntypedFormControl({ value: this.email, disabled: false }, [
          Validators.required,
          Validators.maxLength(254),
        ]),
      });
    }
    return this.form;
  }

  get fromForm(): ForgotPassword {
    let value = this.form?.value;

    if (value != undefined) this.email = value.email;

    return this;
  }
}

export class ChangePassword {
  oldPassword: string;
  newPassword: string;
  user: User;
  constructor(oldPassword: string, newPassword: string, user: User) {
    this.oldPassword = oldPassword;
    this.newPassword = newPassword;
    this.user = user;
  }
}

export class Token implements IModel {
  accessToken: string;
  refreshToken: string;
  accessExpires: Date;
  refreshExpires: Date;

  constructor(other?: any) {
    this.accessToken = other.accessToken;
    this.refreshToken = other.refreshToken;
    this.accessExpires = new Date(other.accessExpires);
    this.refreshExpires = new Date(other.refreshExpires);

    // TODO: Tratar datas
  }
}

export class NewUser implements IModel, IForm {
  private form?: UntypedFormGroup;
  userId: number = 0;
  name?: string;
  username?: string;
  email?: string;
  telephone: string = '+5599999999999';
  providerType: number = 3;
  grandType: number = 0;
  canImpersonate: boolean = false;

  constructor() {}

  get asForm(): UntypedFormGroup {
    if (this.form == null) {
      this.form = new UntypedFormGroup({
        userId: new UntypedFormControl(
          { value: this.userId, disabled: false },
          []
        ),
        name: new UntypedFormControl({ value: this.name, disabled: false }, [
          Validators.required,
          Validators.maxLength(254),
        ]),
        username: new UntypedFormControl(
          { value: this.username, disabled: false },
          [Validators.required, Validators.maxLength(254)]
        ),
        email: new UntypedFormControl({ value: this.email, disabled: false }, [
          Validators.required,
          Validators.maxLength(254),
        ]),
      });
    }
    return this.form;
  }

  get toJson(): Object {
    let json = {
      userId: this.userId,
      name: this.name,
      username: this.username,
      email: this.email,
      telephone: this.telephone,
      providerType: this.providerType,
      grandType: this.grandType,
      canImpersonate: this.canImpersonate,
    };
    return json;
  }

  get fromForm(): NewUser {
    let value = this.form?.value;

    if (value != undefined) {
      this.userId = value.userId;
      this.email = value.email;
      this.name = value.name;
      this.username = value.username;
    }
    return this;
  }
}
